<script setup lang="ts">
import { toRefs, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ProductGroup } from '@/services/pcsServiceTypes'
import { getSiteBaseUrl } from '@/lib/siteBaseUrl'

const { t } = useI18n()

const props = defineProps<{
	product: ProductGroup
	showRawTitle: boolean
}>()

const { showRawTitle } = toRefs(props)

const displayedImage = computed(() => {
	return (
		props.product.image ||
		'https://test-hk.buynship.com/contents/uploads/2024/02/pcs-default-image.png'
	)
})

const displayedPrice = computed(() => {
	return Number(props.product.lowest_price).toLocaleString(undefined, {
		minimumFractionDigits: 2,
	})
})

const displayedSourcesCount = computed(() => {
	const { count } = props.product
	if (!count) return t('pcs_no_source_found')

	const sourceCount = count > 99 ? '99+' : count
	if (count === 1) return t('pcs_one_source_found')
	return t('pcs_multiple_sources_found', { count: sourceCount })
})

const targetUrl = computed(() => {
	const currentUrlParams = new URLSearchParams(window.location.search)
	const categories = currentUrlParams.get('categories')
	const location = currentUrlParams.get('location')

	const newUrlParams = new URLSearchParams({ id: props.product.sku })
	if (categories) newUrlParams.set('categories', categories)
	if (location) newUrlParams.set('location', location)

	const isProduction = import.meta.env.PUBLIC_ENVIRONMENT === 'production'
	const rootUrl = isProduction
		? getSiteBaseUrl()
		: import.meta.env.PUBLIC_ROOT_URL
	const productionPrefix = isProduction ? '/shop' : ''

	return `${rootUrl}${productionPrefix}/products/?${newUrlParams.toString()}`
})
</script>

<template>
	<a
		:href="targetUrl"
		class="cursor-pointer rounded-lg border border-n-100 text-n-950"
	>
		<div class="flex h-full flex-col">
			<img
				:srcset="displayedImage"
				:alt="product.title"
				class="aspect-square w-full rounded-lg object-contain lg:aspect-auto lg:h-[205px]"
			/>
			<div class="flex flex-grow flex-col gap-12px p-12px">
				<div class="typography-s2-emp flex-grow">
					{{ showRawTitle ? product.raw_title : product.title }}
				</div>
				<div>
					<div class="typography-c2-reg text-n-500">
						{{ t('pcs_result_price_start_from') }}
					</div>
					<div class="typography-b2-str">
						<span>HK$ {{ displayedPrice }}</span>
						<span>&nbsp;{{ t('pcs_result_price_start_end') }}</span>
					</div>
				</div>
				<div class="typography-c2-reg text-n-500">
					{{ displayedSourcesCount }}
				</div>
			</div>
		</div>
	</a>
</template>
